import React, { useState, useEffect, useCallback } from "react";
import "views/contractView/styles/newElementFormStyles.css";
import { Button } from "@ds/ds-snam/react";
import { ClientTypes, DsLayout, IClientTypeData, ResponseCode, ResponseStatus, RoutePath } from "shared/constants";
import { ISelectLabel } from "@ds/ds-snam/dist/models/select";
import { IRadioButton } from "@ds/ds-snam/dist/models/radioButton";
import { ICustomer } from "dataModels/contract";
import { DSInput, DSSelectList } from "@ds/ds-snam/dist/index";
import CustomerFormDataInput from "./customerFormDataInput";
import { Select } from "@ds/ds-snam/react";
import _ from "lodash";
import { Container, Row, Col } from "react-grid-system";
import CustomerFormReview from "views/contractView/components/customerFormReview";
import { INotificationAction, NotificationsStatusType } from "dataModels/stateModels/notification";
import { useDispatch, useSelector } from "react-redux";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { postNewCustomer, putCustomer } from "net/contracts";
import { useNavigate, useParams } from "react-router-dom";
import { showNotification, hideNotification } from "state/reducer/notificationSlice";
import SystemError from "components/SystemError";
import { useTranslation } from "react-i18next";
import { FormMode } from "shared/constants";
import { useLoaderData } from "react-router-dom";
import { dsSelectHandler } from "shared/utils/dsComponentUtils";
import { RootState } from "state/appStore";


const CustomerForm: React.FC = (): JSX.Element => {

    //Customer id - When access to retrieve and edit customer existing information, otherwise undefined
    const { id } = useParams();
    //Loaded customer data when in edit mode
    const customerData: ICustomer | undefined = useLoaderData() as ICustomer;


    //eslint-disable-next-line
    const [formMode, setFormMode] = useState<number>(id ? FormMode.EDIT : FormMode.NEW);

    const { t } = useTranslation(['contract']);

    const [clientTypeOptions, setClientTypeOptions] = useState<ISelectLabel[] | undefined>();
    //eslint-disable-next-line
    const [splitOption, setSplitOption] = useState<IRadioButton[] | undefined>();
    const [formStep, setFormStep] = useState<number>(0);
    const [formError, setFormError] = useState<boolean>(false);
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const headers: string[] = (() => {
        const mode:string = formMode===FormMode.NEW?"new":"edit";
        return t("customer.customerForm.pageHeaders."+mode, { returnObjects: true }) as string[];
    })();

    const titles: string[] = (() => {
        //return ["Inserisci le informazioni dell’azienda cliente", "Riepilogo cliente"];
        return t("customer.customerForm.title", { returnObjects: true }) as string[];
    })();

    const prevStep = () => {
        const nextStep = formStep - 1;
        setFormStep(nextStep);
    }
    const nextStep = () => {
        const nextStep = formStep + 1;
        setFormStep(nextStep);
    }

    //FormMode new - empty customer
    const getFormData = (clientTypeValue: string = ""): ICustomer => {
        const defaultFormData: ICustomer = {
            id: '',
            clientType: clientTypeValue, //tipologia azienda, 0 - IT, 1 - UE, 2 - Extra UE
            businessName: '', //nome azienda
            businessSecondName: '', //ragione sociale azienda
            businessAddress: '', //indirizzo azienda
            businessStreetNumber: '', //numero civico azienda
            businessPostalCode: '', //CAP azienda
            businessCity: '', //città azienda
            businessProvince: '', //provincia azienda
            businessState: '', //stato azienda
            inipec: '', //INIPEC
            billingPec: '', //PEC fatturazione
            billingEmail: '', //email fatturazione
            businessEmail: '', //email aziendale
            recipientCode: '', //codice destinatario
            fiscalCode: '', //codice fiscale
            vat: '', //partita iva
            vatCee: '', //partita iva CEE
            vatExtrCee: '', //partita iva Extra CEE
            splitPayment: false, //booleano per divisione pagamento
            vatGroup: '', //gruppo iva
            taxAgentDenomination: '', //denominazione rappresentante fiscale
            taxAgentVat: '', //partita iva rappresentante fiscale
            taxAgentFirstName: '', //nome rappresentante fiscale
            taxAgentSecondName: '', //cognome rappresentante fiscale
            taxAgentState: '', // stato rappresentante fiscale
            bankIban: '', //codice IBAN
            bankState: '', //stato banca,
            bankAccount: '',// conto corrente
            bankSwift: '',//codice SWIFT
            businessCode: '',
            carried_delegations: [], //deleghe effettuate
            received_delegations: [], //deleghe ricevute
            isccDocument: {
                id: "",
                businessId: "",
                documents: []
            } //certificazione iscc
        };
        return defaultFormData;
    }

    useEffect(() => {
        return () => {
            //To be sure is not spins at exit
            dispatch(requestPristine())
        }
    });

    const [formData, setFormData] = useState<ICustomer>(formMode === FormMode.NEW ? getFormData() : customerData);



    useEffect(() => {
        //Select type
        let options: ISelectLabel[] = [];
        ClientTypes.forEach((e: IClientTypeData) => {
            //console.log(e.value,formData.clientType,e.value===formData.clientType.toString());
            //In case of edit, it needs to be preselected with actual form data, false otherwise
            const isSelected: boolean = formMode === FormMode.EDIT && e.value === formData.clientType;
            options.push({ id: e.value, label: e.label, value: e.value, selected: isSelected });
        });
        setClientTypeOptions(options);

        //Radio
        setSplitOption([{ "id": "0", "label": "Si", "value": "0" }, { "id": "1", "label": "No", "value": "1" }]);
        //eslint-disable-next-line
    }, []);

    //On new formmode, reset fields when clientType changes
    /*useEffect(() => {
        if (formMode === FormMode.NEW) {
            const emptyFormData = getFormData(formData.clientType);
            setFormData(emptyFormData);
        }
    }, [formData.clientType, formMode]);*/
    useEffect(() => {
        if (formMode === FormMode.NEW) {
            // Ottieni i dati vuoti per il tipo di client, ma mantieni invariati businessState e businessProvince
            const emptyFormData = getFormData(formData.clientType);
    
            // Mantenere businessState e businessProvince invariati
            setFormData(prevData => ({
                ...emptyFormData, // Aggiungi i dati vuoti
                businessState: prevData.businessState, // Mantieni il valore di businessState
                businessProvince: prevData.businessProvince // Mantieni il valore di businessProvince
            }));
        }
    }, [formData.clientType, formMode]);


    const onSelectInputChange = (event: Event,fieldName:String) => {
        const selection: ISelectLabel = (event as CustomEvent).detail.option;
        /*let currentFormData = { ...formData };
        (currentFormData[fieldName as keyof ICustomer] as string) = selection?.value!;
        setFormData(currentFormData);*/

        setFormData(prevFormData => ({
            ...prevFormData,
            [fieldName as keyof ICustomer]: selection?.value || "" // Aggiorna il campo specificato con il nuovo valore
        }));
    }

    const onTextInputChange = (inputEvent: Event) => {
        const fieldName = (inputEvent as CustomEvent).detail.input.name;
        let currentData: ICustomer = formData;
        (currentData[fieldName as keyof ICustomer] as string) = (inputEvent as CustomEvent).detail.input.value;
        setFormData(currentData);
        if (["billingEmail", "businessEmail", "inipec", "billingPec"].includes(fieldName)) {
            validateEmailField(fieldName);
        }
    }

    const onRadioChange = (event: Event) => {
        //console.log((event as CustomEvent).detail.selectedRadio);
        const selection: string = (event as CustomEvent).detail.selectedRadio.value;
        let currentFormData = { ...formData };
        currentFormData.splitPayment = (selection === "0") ? true : false;
        setFormData(currentFormData);
    }


    /*const validateStep = (): number => {
            const excludeList: string[] = [
                "vatGroup",
                "taxAgentDenomination",
                "taxAgentVat",
                "taxAgentFirstName",
                "taxAgentSecondName",
                "taxAgentState",
                "businessCode"
            ];
        
            const emailFields: string[] = ["billingEmail", "businessEmail", "inipec","billingPec"];
            //const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Regex per validare email
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
            let errors: number = 0;
        
            const mandatoryFields: string[] = _.filter(Object.keys(formData), (fieldName: string) => {
                return excludeList.indexOf(fieldName) === -1;
            });
        
            // Controllo per ogni campo obbligatorio
            mandatoryFields.forEach((key: string) => {
                const elem: DSInput | null = document.querySelector(`[input-id='${key}']`);
        
                if (elem) {
                    const isFieldEmpty: boolean = formData[key as keyof ICustomer] === "";
                    const isEmailInvalid: boolean = emailFields.includes(key) && !emailRegex.test(formData[key as keyof ICustomer] as string);
        
                    // Controlla se il campo è vuoto o se l'email è in un formato errato
                    const koCheck: boolean = isFieldEmpty || isEmailInvalid;
        
                    elem.inputStatus = koCheck ? 'error' : 'default';
        
                    if (koCheck) {
                        errors++;
                    }
                }
            });
        
            // Controllo per i campi Select (provincia e nazione)
            const provinceField: DSSelectList | null = document.querySelector("[id='id-select-province']");
            const nationField: DSSelectList | null = document.querySelector("[id='id-select-nation']");
        
            if (provinceField) {
                const isProvinceEmpty = formData.businessProvince === "";
                provinceField.selectStatus = isProvinceEmpty ? "error" : "default";
                if (isProvinceEmpty) errors++;
            }
        
            if (nationField) {
                const isNationEmpty = formData.businessState === "";
                nationField.selectStatus = isNationEmpty ? "error" : "default";
                if (isNationEmpty) errors++;
            }
        
            const selElem: DSSelectList | null = document.querySelector("[id='clientTypeSelector']");
            if (selElem) {
                const selCheck = formData.clientType === "";
                selElem.selectStatus = selCheck ? "error" : "default";
                if (selCheck) {
                    errors++;
                }
            }
        
            return errors;
        };*/
        
        const validateEmailField = (fieldName: string): void => {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            const value = formData[fieldName as keyof ICustomer] as string;
            const elem: DSInput | null = document.querySelector(`[input-id='${fieldName}']`);
        
            if (elem) {
                const isInvalid = value === "" || !emailRegex.test(value);
                elem.inputStatus = isInvalid ? "error" : "default";  // Imposta solo lo stato per il campo specifico
            }
        };

    const validateStep = (): number => {
            const excludeList: string[] = [
                "vatGroup",
                "taxAgentDenomination",
                "taxAgentVat",
                "taxAgentFirstName",
                "taxAgentSecondName",
                "taxAgentState",
                /*"businessCode"*/
            ];
        
            
            let errors: number = 0;
            const emailFields: string[] = ["billingEmail", "businessEmail", "inipec", "billingPec"];
            const mandatoryFields: string[] = _.filter(Object.keys(formData), (fieldName: string) => {
                return excludeList.indexOf(fieldName) === -1 && !emailFields.includes(fieldName);
            });
        
            // Controllo campi obbligatori (esclusi quelli email)
            mandatoryFields.forEach((key: string) => {
                const elem: DSInput | null = document.querySelector(`[input-id='${key}']`);
                if (elem) {
                    const isFieldEmpty: boolean = formData[key as keyof ICustomer] === "";
                    elem.inputStatus = isFieldEmpty ? 'error' : 'default';
                    if (isFieldEmpty) {
                        errors++;
                    }
                }
            });
        
            // Validazione email separata
            // Verifica email separata
            emailFields.forEach(field => validateEmailField(field));
        
            // Controllo campi Select
            const provinceField: DSSelectList | null = document.querySelector("[id='id-select-province']");
            const nationField: DSSelectList | null = document.querySelector("[id='id-select-nation']");
            const selElem: DSSelectList | null = document.querySelector("[id='clientTypeSelector']");
        
            if (provinceField) {
                const isProvinceEmpty = formData.businessProvince === "";
                provinceField.selectStatus = isProvinceEmpty ? "error" : "default";
                if (isProvinceEmpty) errors++;
            }
        
            if (nationField) {
                const isNationEmpty = formData.businessState === "";
                nationField.selectStatus = isNationEmpty ? "error" : "default";
                if (isNationEmpty) errors++;
            }
        
            if (selElem) {
                const selCheck = formData.clientType === "";
                selElem.selectStatus = selCheck ? "error" : "default";
                if (selCheck) errors++;
            }
        
            return errors;
    };

    const saveFormData = useCallback(() => {
        const translationField: string = formMode === FormMode.NEW ? "new" : "edit";
        const request = formMode === FormMode.EDIT ? putCustomer : postNewCustomer;
        let popUpAction: INotificationAction;
        dispatch(requestPending());
        // TODO: GESTIONE ERRORI ####
        request(formData)
            .then((response) => {
                const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? "Warning": t(`customer.customerForm.successToast.${translationField}.title`) );
                const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t(`customer.customerForm.successToast.${translationField}.subTitle`);
                const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                
                popUpAction = {
                    show: true,
                    title: popUpTitle,//"Cliente inserito correttamente",
                    description: popUpDescription,//"Il profilo del cliente è stato inserito correttamente in piattaforma, ora puoi inserire i relativi contratti",
                    status: popUpStatus,
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
                if(formMode===FormMode.NEW){
                    navigate(RoutePath.CONTRACTS_CUSTOMER_INFO);
                }else{//edit
                    navigate(RoutePath.CONTRACTS_CUSTOMER_INFO+"/"+id);
                }
                
            })
            .catch((error) => {
                console.log(error);
                setFormError(true);//error step
            })
            .finally(() => {
                dispatch(requestPristine());
            })

    }, [formData, dispatch, navigate, t, formMode]);


    return (
        <>
            <p className="pageHeader">
                {headers[formStep]}
            </p>
            {formStep!==1 &&
            <div className="listTableContainerGrey">
                {formError ?
                    <SystemError link={RoutePath.CONTRACTS_CUSTOMER_INFO}/>
                : 
                <>
                        <Row>
                            <Col>
                                <p className="pageSemiTitle">
                                    {titles[formStep]}
                                </p>
                            </Col>
                        </Row>

                        {formStep === 0 && <>
                            <Row>
                                <Col xs={12} md={4}>
                                    <div className="textInputWrapper">
                                        <Select
                                            id="clientTypeSelector"
                                            hasIcon={false}
                                            menuOptions={clientTypeOptions ? clientTypeOptions : []}
                                            menuLabel={t('customer.customerForm.sections.info.fields.0')}//"Tipologia"
                                            menuId="select-id"
                                            isDisabled={formMode === FormMode.EDIT}
                                            selectStatus="default"
                                            menuPlaceholder={t('customer.customerForm.sections.info.fields.0')}//"Tipologia"
                                            onOptionSelection={(event:Event)=>{
                                                const selection: ISelectLabel = (event as CustomEvent).detail.option;
                                                dsSelectHandler(clientTypeOptions!, selection);
                                                onSelectInputChange(event,'clientType');
                                            }}
                                        ></Select>
                                    </div>
                                </Col>
                                <Col xs={12} md={4}></Col>
                                <Col xs={12} md={4}></Col>
                            </Row>

                            <CustomerFormDataInput
                                onRadioChange={onRadioChange}
                                onTextInputChange={onTextInputChange}
                                onSelectInputChange={onSelectInputChange}
                                formData={formData}
                            />
                        </>}

                        
                    </>
                }
            </div>
            }
            {formError ?
                    <SystemError link={RoutePath.CONTRACTS_CUSTOMER_INFO} />
            : 
                <>
                    {formStep === 1 &&
                        <CustomerFormReview formData={formData} />
                    }
                </>
            }
            

            {!formError && <div className="buttonContainer">
                {formStep === 0 && <Button
                    id="nextButton"
                    buttonType="button"
                    buttonUIVariant="primary"
                    buttonSize="medium"
                    buttonLabel={t('general.buttons.next', { ns: 'app' })}//"Continua"
                    buttonIcon=""
                    isDisabled={formData.clientType===''?true:false}
                    onClickEvent={() => {
                        let errors: number = validateStep();
                        //console.log(errors)
                        if (errors === 0)
                            nextStep();
                    }}
                />
                }

                {formStep === 1 && <>
                    <Button
                        id="backButton"
                        buttonType="button"
                        buttonUIVariant="secondary"
                        buttonSize="medium"
                        buttonLabel={t('general.buttons.back', { ns: 'app' })}//"Indietro"
                        buttonIcon=""
                        isDisabled={false}
                        onClickEvent={prevStep}
                        style={{ marginRight: "8px" }}
                    />
                    <Button
                        buttonType="button"
                        buttonUIVariant="primary"
                        buttonSize="medium"
                        buttonLabel={t('general.buttons.confirm', { ns: 'app' })}//"Conferma"
                        buttonIcon=""
                        isDisabled={false}
                        onClickEvent={(event: Event) => {
                            //console.log(formData)
                            saveFormData();
                        }}
                    />
                </>
                }
            </div>}

        </>

    )
}

export default CustomerForm;